import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Container, Col, Row } from 'reactstrap';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-enterprise';
import config from '../../../../../config';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import DateRangePicker from '../../../../../shared/components/widgets/DateRangePicker';
import { currencyFormatter, formatNumberDefaultToZero } from '../../../../../shared/components/table/functions';

export default function PathReportLayout({ title }) {
  const gridRef = useRef();
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [apiLoad, setApiLoad] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(9, 'days')); // moment('20231005', 'YYYYMMDD')
  const [endDate, setEndDate] = useState(moment().subtract(2, 'days'));

  const [apiData, setApiData] = useState([]);

  useEffect(() => {
    setApiLoad(true);
    // setGraphData([{}]);
    setApiData([{}]);

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const reportUrl = `${config.serverUrl}/amc/path-report?accountId=${sellerSelect.value}&startDate=${startDate.format('YYYYMMDD')}&endDate=${endDate.format('YYYYMMDD')}`;
    fetch(reportUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then((data) => {
        setApiData(data);
        setApiLoad(false);
      }).catch(() => {
        setApiLoad(false);
      });
  }, [sellerSelect.value, startDate, endDate]);

  function buildMasters(rows) {
    const toNum = (v) => (v == null ? 0 : Number(v) || 0);
    // const safeDiv = (n, d) => (d ? n / d : null);

    const source = Array.isArray(rows) ? rows : [];
    const byKey = new Map();

    for (let i = 0; i < source.length; i += 1) {
      const r = source[i] || {};
      const key = `${String(r.path)}`;

      let group = byKey.get(key);
      if (!group) {
        group = {
          path: r.path || null,

          path_occurrences: 0,
          impressions: 0,
          cost: 0,
          users_that_purchased: 0,
          sales_amount: 0,
          purchases: 0,
          ntb_users_that_purchased: 0,
          ntb_sales_amount: 0,
          ntb_purchases: 0,

          // ofa_cpm: null,
          // ofa_cpc: null,
          // ofa_cac: null,
          // ofa_roas: null,

          daily: [],
        };
        byKey.set(key, group);
      }

      group.path_occurrences += toNum(r.path_occurrences);
      group.impressions += toNum(r.impressions);
      group.cost += toNum(r.cost);
      group.users_that_purchased += toNum(r.users_that_purchased);
      group.sales_amount += toNum(r.sales_amount);
      group.purchases += toNum(r.purchases);
      group.ntb_users_that_purchased += toNum(r.ntb_users_that_purchased);
      group.ntb_sales_amount += toNum(r.ntb_sales_amount);
      group.ntb_purchases += toNum(r.ntb_purchases);

      group.daily.push(r);
    }

    const masters = Array.from(byKey.values());

    for (let i = 0; i < masters.length; i += 1) {
      const g = masters[i];

      // g.ofa_cpm = safeDiv(g.total_ofa_spend * 1000, g.total_ofa_impressions);
      // g.ofa_cpc = safeDiv(g.total_ofa_spend, g.total_ofa_page_views); // change denominator if you have "clicks"
      // g.ofa_cac = safeDiv(g.total_ofa_spend, g.total_ofa_orders); // or use conversions if preferred
      // g.ofa_roas = safeDiv(g.total_ofa_sales, g.total_ofa_spend);

      g.daily.sort((a, b) => {
        const da = a && a.data_date ? String(a.data_date) : '';
        const db = b && b.data_date ? String(b.data_date) : '';
        if (da < db) return -1;
        if (da > db) return 1;
        return 0;
      });
    }

    masters.sort((a, b) => {
      const aNum = parseInt(a.path_occurrences, 10);
      const bNum = parseInt(b.path_occurrences, 10);
      if (aNum < bNum) return 1;
      if (aNum > bNum) return -1;
      return 0;
    });

    return masters;
  }

  const masters = useMemo(
    () => buildMasters(apiData || []),
    [apiData],
  );

  const masterCols = useMemo(() => [
    { field: 'path', headerName: 'Path Bucket', cellRenderer: 'agGroupCellRenderer', pinned: 'left' },
    { field: 'path_occurrences', headerName: 'Path Occurrences', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'impressions', headerName: 'Impressions', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'cost', headerName: 'Spend', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'users_that_purchased', headerName: 'Users That purchased', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'sales_amount', headerName: 'Sales Amount', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'purchases', headerName: 'Purchases', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'ntb_users_that_purchased', headerName: 'NTB Users That Purchased', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'ntb_sales_amount', headerName: 'NTB Sales Amount', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'ntb_purchases', headerName: 'NTB Purchases', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
  ], []);


  const detailCellRendererParams = useMemo(() => ({
    detailGridOptions: {
      columnDefs: [
        { field: 'data_date', headerName: 'Date' },
        { field: 'path_occurrences', headerName: 'Path Occurrences', valueFormatter: formatNumberDefaultToZero },
        { field: 'impressions', headerName: 'Impressions', valueFormatter: formatNumberDefaultToZero },
        { field: 'cost', headerName: 'Spend', valueFormatter: currencyFormatter },
        { field: 'users_that_purchased', headerName: 'Users That purchased', valueFormatter: formatNumberDefaultToZero },
        { field: 'sales_amount', headerName: 'Sales Amount', valueFormatter: currencyFormatter },
        { field: 'purchases', headerName: 'Purchases', valueFormatter: formatNumberDefaultToZero },
        { field: 'ntb_users_that_purchased', headerName: 'NTB Users That Purchased', valueFormatter: formatNumberDefaultToZero },
        { field: 'ntb_sales_amount', headerName: 'NTB Sales Amount', valueFormatter: currencyFormatter },
        { field: 'ntb_purchases', headerName: 'NTB Purchases', valueFormatter: formatNumberDefaultToZero },
      ],
      defaultColDef: { flex: 1, sortable: true, resizable: true },
    },
    getDetailRowData: (params) => {
      params.successCallback(params.data.daily || []);
    },
  }), []);

  function dateChange(start, end) {
    setStartDate(moment(start));
    setEndDate(moment(end));
  }

  // Table Settings
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    flex: 1,
  }), []);

  const onGridReady = useCallback(() => {
    gridRef.current.api.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }, []);

  let useTheme = 'ag-theme-balham';
  if (!theme.className.endsWith('light')) {
    useTheme = 'ag-theme-balham-dark';
  }

  const onBtnExport = () => {
    const fileName = `${sellerSelect.value}-AMC-Path-${moment(startDate).format('YYYYMMDD')}-${moment(endDate).format('YYYYMMDD')}.csv`;
    gridRef.current.api.exportDataAsCsv({ fileName });
  };

  return (
    <Container className="dashboard">
      <Row>
        <Col>
          <CardPanel
            lg={12}
            md={12}
            title=""
            subhead=""
            showButtons={false}
            parentRefresh={apiLoad}
          >
            <Row>
              <Col md={12}>
                <h3 className="page-title">{title}</h3>
              </Col>
            </Row>
            <Row xs={1} md={2} className="align-items-center g-3 p-3 p-md-0">
              {/* left col: center on mobile, normal on md+ */}
              <Col className="d-flex justify-content-center justify-content-md-start text-center text-md-start">
                <DateRangePicker
                  startDate={startDate.toDate()}
                  endDate={endDate.toDate()}
                  onDateChange={dateChange}
                />
              </Col>

              {/* right col: add top padding only when stacked, center on mobile, right on md+ */}
              <Col className="d-flex justify-content-center justify-content-md-end pt-3 pt-md-0">
                <button
                  className="btn btn-outline-primary"
                  style={{ width: 200 }}
                  onClick={onBtnExport}
                  type="button"
                >
                  Export
                </button>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className={useTheme} style={{ height: '50vh', width: '100%' }}>
                  <AgGridReact
                    ref={gridRef}
                    rowData={masters}
                    columnDefs={masterCols}
                    defaultColDef={defaultColDef}
                    masterDetail
                    isRowMaster={(dataItem) => Array.isArray(dataItem?.daily) && dataItem.daily.length > 0}
                    detailCellRendererParams={detailCellRendererParams}
                    detailRowAutoHeight
                    groupSuppressAutoColumn={false}
                    animateRows
                    groupIncludeTotalFooter
                    onFirstDataRendered={(p) => p.api.forEachNode(n => n.setExpanded(false))}
                    onGridReady={onGridReady}
                  />
                </div>
              </Col>
            </Row>
          </CardPanel>
        </Col>
      </Row>
    </Container>
  );
}
